/**
 * Palworld breeding calculator (Palworld 1.0 rules, data from the Palworld Wiki).
 *
 *  1. Two of the same species produce that species.
 *  2. Specific parent pairs always produce a fixed child ("special combos"). In 1.0 there are
 *     well over a thousand; a couple depend on which parent is male (Katress × Wixen).
 *  3. Otherwise: target = floor((rankA + rankB + 1) / 2) and the child is the Pal that can hatch
 *     from the formula (`child_eligble`) whose breeding power is closest to the target.
 *     An exact tie goes to the Pal with the higher duplicate priority (the game's tie-break).
 *  4. Pals with `breedable: false` can't be used at the Breeding Farm at all.
 */

export interface BreedablePal {
  id: number;
  key: string;
  name: string;
  image: string;
  breeding: {
    rank: number;
    priority: number;
    breedable: boolean;
    child_eligble: boolean;
    male_probability: number;
  };
}

export type Gender = "male" | "female";
export interface ComboDef {
  a: string;
  b: string;
  child: string;
  aGender?: Gender | null;
  bGender?: Gender | null;
}

export interface ParentPair<P extends BreedablePal = BreedablePal> {
  a: P;
  b: P;
  child: P;
  /** true when the result comes from a special combo rather than the formula */
  unique: boolean;
  /** set when the outcome depends on which parent is male/female */
  aGender?: Gender | null;
  bGender?: Gender | null;
}

export interface BreedingStep<P extends BreedablePal = BreedablePal> {
  a: P;
  b: P;
  child: P;
  aGender?: Gender | null;
  bGender?: Gender | null;
}

export interface BreedingRoute<P extends BreedablePal = BreedablePal> {
  target: P;
  /** Steps in the order they should be done; empty if the target is already owned. */
  steps: BreedingStep<P>[];
}

type Outcome<P> = { child: P; unique: boolean; aGender?: Gender | null; bGender?: Gender | null };

const pairKey = (x: number, y: number) => (x < y ? `${x}|${y}` : `${y}|${x}`);

export class BreedingCalculator<P extends BreedablePal = BreedablePal> {
  readonly pals: P[];
  private byId = new Map<number, P>();
  private byName = new Map<string, P>();
  private byKey = new Map<string, P>();
  /** formula-eligible pals sorted by rank ascending */
  private eligible: P[];
  /** special combos by unordered pair, child + parent genders oriented to (lower id, higher id) */
  private unique = new Map<string, { child: P; lowGender: Gender | null; highGender: Gender | null }[]>();
  private outcomeCache = new Map<string, Outcome<P>[]>();
  private parentsCache?: Map<number, ParentPair<P>[]>;
  readonly comboCount: number;

  constructor(pals: P[], combos: ComboDef[] = []) {
    this.pals = pals;
    for (const p of pals) {
      this.byId.set(p.id, p);
      this.byName.set(p.name.toLowerCase(), p);
      this.byKey.set(p.key.toLowerCase(), p);
    }
    this.eligible = pals
      .filter((p) => p.breeding.breedable && p.breeding.child_eligble)
      .sort((x, y) => x.breeding.rank - y.breeding.rank);

    let n = 0;
    for (const c of combos) {
      const a = this.get(c.a);
      const b = this.get(c.b);
      const child = this.get(c.child);
      if (!a || !b || !child) continue;
      const k = pairKey(a.id, b.id);
      const aLow = a.id <= b.id;
      const entry = {
        child,
        lowGender: (aLow ? c.aGender : c.bGender) ?? null,
        highGender: (aLow ? c.bGender : c.aGender) ?? null,
      };
      const list = this.unique.get(k) ?? [];
      if (!list.some((e) => e.child.id === child.id && e.lowGender === entry.lowGender)) list.push(entry);
      this.unique.set(k, list);
      n++;
    }
    this.comboCount = n;
  }

  get(idOrName: number | string): P | undefined {
    if (typeof idOrName === "number") return this.byId.get(idOrName);
    const k = idOrName.toLowerCase();
    return this.byName.get(k) ?? this.byKey.get(k);
  }

  isUniquePair(a: P, b: P): boolean {
    return this.unique.has(pairKey(a.id, b.id));
  }

  /** Breedable Pal whose rank is closest to `target` (ties → higher priority). */
  private closestByRank(target: number): P | undefined {
    const list = this.eligible;
    if (!list.length) return undefined;
    let lo = 0;
    let hi = list.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (list[mid]!.breeding.rank < target) lo = mid + 1;
      else hi = mid;
    }
    // gather every candidate at the minimum distance (several Pals can share a rank)
    let best: P | undefined;
    let bestDist = Infinity;
    for (let i = Math.max(0, lo - 3); i < Math.min(list.length, lo + 3); i++) {
      const p = list[i]!;
      const d = Math.abs(p.breeding.rank - target);
      if (d < bestDist || (d === bestDist && best && p.breeding.priority > best.breeding.priority)) {
        best = p;
        bestDist = d;
      }
    }
    return best;
  }

  /** Every possible child of a × b (more than one only for gender-specific combos). */
  outcomes(a: P, b: P): Outcome<P>[] {
    if (!a.breeding.breedable || !b.breeding.breedable) return [];
    const key = pairKey(a.id, b.id);
    // cache is stored oriented to (lower id, higher id); flip genders when a is the higher id
    let base = this.outcomeCache.get(key);
    if (!base) {
      const u = this.unique.get(key);
      if (u?.length) {
        base = u.map((e) => ({ child: e.child, unique: true, aGender: e.lowGender, bGender: e.highGender }));
      } else if (a.id === b.id) {
        base = [{ child: a, unique: false }];
      } else {
        const c = this.closestByRank(Math.floor((a.breeding.rank + b.breeding.rank + 1) / 2));
        base = c ? [{ child: c, unique: false }] : [];
      }
      this.outcomeCache.set(key, base);
    }
    if (a.id <= b.id) return base;
    return base.map((o) => ({ ...o, aGender: o.bGender, bGender: o.aGender }));
  }

  /** The child of a × b (for gender-specific pairs, the first listed outcome), or null if they can't breed. */
  child(a: P, b: P): P | null {
    return this.outcomes(a, b)[0]?.child ?? null;
  }

  /** Every unordered parent pair that produces `child` (self pair only if it's the only way). */
  parentsOf(child: P): ParentPair<P>[] {
    if (!this.parentsCache) {
      const map = new Map<number, ParentPair<P>[]>();
      const pals = this.pals.filter((p) => p.breeding.breedable);
      for (let i = 0; i < pals.length; i++) {
        for (let j = i; j < pals.length; j++) {
          const a = pals[i]!;
          const b = pals[j]!;
          for (const o of this.outcomes(a, b)) {
            const entry: ParentPair<P> = { a, b, child: o.child, unique: o.unique, aGender: o.aGender, bGender: o.bGender };
            const arr = map.get(o.child.id);
            if (arr) arr.push(entry);
            else map.set(o.child.id, [entry]);
          }
        }
      }
      this.parentsCache = map;
    }
    const all = this.parentsCache.get(child.id) ?? [];
    const nonSelf = all.filter((p) => p.a.id !== p.b.id);
    return nonSelf.length ? nonSelf : all;
  }

  /** What `a` produces with every other Pal. */
  offspringOf(a: P): ParentPair<P>[] {
    const out: ParentPair<P>[] = [];
    for (const b of this.pals)
      for (const o of this.outcomes(a, b)) out.push({ a, b, child: o.child, unique: o.unique, aGender: o.aGender, bGender: o.bGender });
    return out;
  }

  /** All results of one breeding between two different Pals in `pool`. */
  poolResults(pool: P[]): ParentPair<P>[] {
    const out: ParentPair<P>[] = [];
    for (let i = 0; i < pool.length; i++)
      for (let j = i + 1; j < pool.length; j++) {
        const a = pool[i]!;
        const b = pool[j]!;
        for (const o of this.outcomes(a, b)) out.push({ a, b, child: o.child, unique: o.unique, aGender: o.aGender, bGender: o.bGender });
      }
    return out;
  }

  /**
   * Fewest breedings to get `target` from the Pals in `pool`.
   * Cost(owned) = 0, cost(bred) = cost(a) + cost(b) + 1, solved exactly with Knuth's
   * generalisation of Dijkstra. Returns null when the target can't be reached.
   */
  findRoute(pool: P[], target: P): BreedingRoute<P> | null {
    if (pool.some((p) => p.id === target.id)) return { target, steps: [] };
    if (pool.length === 0) return null;

    const cost = new Map<number, number>();
    const via = new Map<number, { a: P; b: P; aGender?: Gender | null; bGender?: Gender | null }>();
    const settled: P[] = [];
    const settledSet = new Set<number>();
    for (const p of pool) cost.set(p.id, 0);

    for (;;) {
      let best: P | undefined;
      let bestCost = Infinity;
      for (const [id, c] of cost) {
        if (!settledSet.has(id) && c < bestCost) {
          bestCost = c;
          best = this.byId.get(id);
        }
      }
      if (!best) break;
      settledSet.add(best.id);
      settled.push(best);
      if (best.id === target.id) break;

      for (const s of settled) {
        if (s.id === best.id) continue;
        for (const o of this.outcomes(best, s)) {
          if (settledSet.has(o.child.id)) continue;
          const nc = bestCost + cost.get(s.id)! + 1;
          if (nc < (cost.get(o.child.id) ?? Infinity)) {
            cost.set(o.child.id, nc);
            via.set(o.child.id, { a: best, b: s, aGender: o.aGender, bGender: o.bGender });
          }
        }
      }
    }
    if (!settledSet.has(target.id)) return null;

    const steps: BreedingStep<P>[] = [];
    const done = new Set<number>(pool.map((p) => p.id));
    const build = (p: P) => {
      if (done.has(p.id)) return;
      const v = via.get(p.id)!;
      build(v.a);
      build(v.b);
      steps.push({ a: v.a, b: v.b, child: p, aGender: v.aGender, bGender: v.bGender });
      done.add(p.id);
    };
    build(target);
    return { target, steps };
  }
}

/** Shared calculator over the site's data. */
export const averageRank = (a: number, b: number) => Math.floor((a + b + 1) / 2);
